import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import * as yup from "yup";
import { IJobCategory } from "../../shared/interfaces/job-category/IJobCategory";
import { JobCategoryService } from "./service";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const schema = yup.object().shape({
    name: yup.string().required("validation.required"),
    isCommon: yup.boolean(),
});

const JobCategoryForm = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<IJobCategory>({
        resolver: yupResolver(schema) as any,
        defaultValues: isEdit
            ? async () => {
                try {
                    return await JobCategoryService.getSingle(+id!);
                } catch (e) {
                    ErrorToast(e);
                    return { name: "", isCommon: false };
                }
            }
            : { name: "", isCommon: false },
    });

    const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

    const onSubmit = async (data: IJobCategory) => {
        try {
            if (isEdit) {
                await JobCategoryService.update(data, +id!);
            } else {
                await JobCategoryService.create(data);
            }
            SuccessToast(
                t(`jobCategory.${isEdit ? "successUpdate" : "successCreate"}`),
            );
            navigate("/job-categories");
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <Row className="g-3">
                                    <Col sm={12} xl={6}>
                                        <Form.Label className="required">
                                            {t("jobCategory.name")}
                                        </Form.Label>
                                        <Controller
                                            name="name"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Form.Control
                                                        type="text"
                                                        isInvalid={!!fieldState.error}
                                                        placeholder={t("jobCategory.enterName")}
                                                        {...field}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(fieldState.error?.message as string)}
                                                    </Form.Control.Feedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col sm={12} xl={6}>
                                        <Form.Label>
                                            {t("jobCategory.isCommon")}
                                        </Form.Label>
                                        <Controller
                                            name="isCommon"
                                            control={control}
                                            render={({ field }) => (
                                                <Form.Check
                                                    type="switch"
                                                    id="isCommon-switch"
                                                    checked={field.value}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                />
                                            )}
                                        />
                                    </Col>
                                    <Col sm={12} className="d-flex gap-2">
                                        <Button type="submit" variant="primary">
                                            {t(`jobCategory.${isEdit ? "update" : "create"}`)}
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={() => guardedNavigate("/job-categories")}>
                                            {t("button.cancel")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {unsavedChangesModal}
        </React.Fragment>
    );
};

export default JobCategoryForm;
